/**
 * workspaceOptionsCache — ONE listMyWorkspaces snapshot shared by every
 * chrome surface (pill, sidebar identity chip, combined-view badges), so
 * workspace identity is consistent everywhere and the callable isn't
 * hammered once per component (multi-org P3/P4, 2026-08-16).
 *
 * SPEED (owner report 2026-08-16: "the workspaces take too long to load at
 * the top right"): workspace switches hard-reload the page, so the module
 * cache died on every switch and the pill waited a full callable round-trip
 * (cold starts included) before first paint. The last verified list is now
 * PERSISTED per-uid and served instantly (stale-while-revalidate): chrome
 * renders from the persisted copy in the same frame while the callable
 * refreshes it in the background. The persisted copy is only ever a list the
 * SERVER verified for this exact uid — never trusted across users, and
 * switching accounts can't leak another user's list.
 *
 * No firebase imports here by design (jsdom leaf-module rule): callers that
 * know the signed-in uid pass it in to unlock persistence; calls without a
 * uid behave exactly as before.
 */
import { listMyWorkspaces, type WorkspaceOption } from './rbacService';

const TTL_MS = 60_000;
const PERSIST_KEY = 'prest0_workspace_options_v1';
/** Persisted copies older than this never paint (hygiene bound). */
const PERSIST_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

let cached: { at: number; options: WorkspaceOption[] } | null = null;
let inflight: Promise<WorkspaceOption[]> | null = null;

function isOptionShape(o: unknown): o is WorkspaceOption {
  const r = o as Record<string, unknown> | null;
  return !!r && typeof r.accountId === 'string' && typeof r.displayName === 'string';
}

function readPersisted(uid: string): WorkspaceOption[] | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { v?: number; uid?: string; at?: number; options?: unknown };
    if (parsed.v !== 1 || parsed.uid !== uid) return null;
    if (typeof parsed.at !== 'number' || Date.now() - parsed.at > PERSIST_MAX_AGE_MS) return null;
    if (!Array.isArray(parsed.options) || !parsed.options.every(isOptionShape)) return null;
    return parsed.options;
  } catch {
    return null;
  }
}

function writePersisted(uid: string, options: WorkspaceOption[]): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(PERSIST_KEY, JSON.stringify({ v: 1, uid, at: Date.now(), options }));
  } catch { /* storage full/blocked — persistence is an optimization */ }
}

function revalidate(persistUid?: string | null): Promise<WorkspaceOption[]> {
  if (inflight) return inflight;
  inflight = listMyWorkspaces()
    .then((res) => {
      const options = res.workspaces ?? [];
      cached = { at: Date.now(), options };
      if (persistUid) writePersisted(persistUid, options);
      return options;
    })
    .catch(() => cached?.options ?? [])
    .finally(() => { inflight = null; });
  return inflight;
}

/**
 * Resolve the caller's workspaces. Pass the signed-in uid when you have it —
 * that unlocks the instant persisted paint; without it the behavior is the
 * original network-first resolution.
 */
export async function fetchWorkspaceOptionsCached(
  persistUid?: string | null,
): Promise<WorkspaceOption[]> {
  if (cached && Date.now() - cached.at < TTL_MS) return cached.options;
  if (persistUid) {
    const persisted = readPersisted(persistUid);
    if (persisted && persisted.length > 0) {
      // Serve instantly; refresh in the background for the next consumer.
      cached = { at: 0, options: persisted }; // at:0 → next call revalidates
      void revalidate(persistUid);
      return persisted;
    }
  }
  return revalidate(persistUid);
}

/**
 * Force a fresh resolution (bypasses the memory TTL; in-flight calls dedupe).
 * Used after mutations that can change the list — e.g. the native-org ensure
 * minting a workspace on first sign-in — so consumers converge on the truth
 * without waiting out the TTL.
 */
export function revalidateWorkspaceOptions(
  persistUid?: string | null,
): Promise<WorkspaceOption[]> {
  if (inflight) {
    // An in-flight resolution may predate the mutation that prompted this
    // call — chain a genuinely fresh fetch behind it instead of handing the
    // caller the possibly-stale promise.
    return inflight.then(() => revalidate(persistUid));
  }
  return revalidate(persistUid);
}

/**
 * Overlay fresh display names (from the live workspace_index listener) onto
 * the memory AND persisted copies, so every later consumer — including the
 * post-switch hard reload's instant paint — starts from the renamed labels
 * instead of resurrecting a stale name.
 */
export function patchCachedDisplayNames(
  persistUid: string | null | undefined,
  names: Record<string, string>,
): void {
  const patch = (options: WorkspaceOption[]): WorkspaceOption[] =>
    options.map((o) => (names[o.accountId] ? { ...o, displayName: names[o.accountId] } : o));
  if (cached) cached = { at: cached.at, options: patch(cached.options) };
  if (persistUid) {
    const persisted = readPersisted(persistUid);
    if (persisted) writePersisted(persistUid, patch(persisted));
  }
}

/**
 * Write-through active-flag patch (org-stickiness round 2, 2026-08-19): a
 * successful workspace switch marks the TARGET active in the memory AND
 * persisted copies BEFORE the hard reload, so the post-reload instant paint
 * (pill, sidebar identity chip, org/subscription pages) shows the org the
 * user just chose — never the pre-switch org revived from a stale
 * `isActive` flag. No-ops when the target is not in the cached list (the
 * revalidate after reload settles it).
 */
export function markActiveWorkspaceInCache(
  persistUid: string | null | undefined,
  accountId: string,
): void {
  const target = (accountId || '').trim();
  if (!target) return;
  const patch = (options: WorkspaceOption[]): WorkspaceOption[] =>
    options.map((o) => ({ ...o, isActive: o.accountId === target }));
  if (cached) cached = { at: cached.at, options: patch(cached.options) };
  if (persistUid) {
    const persisted = readPersisted(persistUid);
    if (persisted) writePersisted(persistUid, patch(persisted));
  }
}

export function clearWorkspaceOptionsCache(): void {
  cached = null;
  inflight = null;
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(PERSIST_KEY);
  } catch { /* ignore */ }
}
